import useAppStore from '../../store/appStore';
import type { SalonInvoiceProfile } from '../../services/peInvoiceFirebaseService';
import { isValidIban } from './ibanValidation';

export interface PeInvoiceSender {
  displayName: string;
  accountHolder: string;
  street: string;
  zip: string;
  city: string;
  email: string;
  phone: string;
  clientIban: string;
}

export interface PeInvoiceTaxIds {
  taxNumber: string;
  vatId: string;
}

const pickNonEmpty = (...values: unknown[]): string => {
  for (const value of values) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const extractVatId = (raw: string): string => {
  const ustMatch =
    raw.match(/USt-?Id\.?\s*:?\s*(DE[A-Z0-9]{9,})/i) || raw.match(/\b(DE[A-Z0-9]{9,})\b/i);
  return ustMatch ? ustMatch[1].toUpperCase() : '';
};

/** Steuernummer & USt-IdNr. aus Mandanten-Stammdaten (wie PERent) */
export function getPeInvoiceTaxFromAppStore(): PeInvoiceTaxIds {
  const cd = (useAppStore.getState() as any)?.clientData || {};
  const nested = cd.clientData || {};

  let taxNumber = pickNonEmpty(cd.steuernummer, nested.steuernummer);
  let vatId = pickNonEmpty(cd.ustIdNr, cd.ustid, nested.ustIdNr, nested.ustid);

  const combined = pickNonEmpty(cd.auslandsteuerId, nested.auslandsteuerId);
  if (combined) {
    const parsedVat = extractVatId(combined);
    if (parsedVat && !vatId) vatId = parsedVat;

    if (!taxNumber) {
      const beforeSeparator = combined.split('|')[0]?.trim() || combined;
      const withoutVat = beforeSeparator
        .replace(/USt-?Id\.?\s*:?\s*DE[A-Z0-9]{9,}/i, '')
        .replace(/\bDE[A-Z0-9]{9,}\b/i, '')
        .trim();
      taxNumber = withoutVat || (parsedVat ? '' : combined);
    }
  }

  return { taxNumber, vatId };
}

/** Firebase-Profil mit Stammdaten-Fallbacks (Adresse, Steuer, Kontakt) */
export function mergePeInvoiceProfileWithStammdaten(
  profile: SalonInvoiceProfile
): SalonInvoiceProfile {
  const sender = getPeInvoiceSenderFromAppStore();
  const tax = getPeInvoiceTaxFromAppStore();
  const storedIban = profile.iban || '';

  return {
    ...profile,
    accountHolder: profile.accountHolder || sender.accountHolder || sender.displayName,
    taxNumber: profile.taxNumber || tax.taxNumber || '',
    vatId: profile.vatId || tax.vatId || '',
    iban: isValidIban(storedIban) ? storedIban : '',
    senderCompanyName:
      profile.senderCompanyName || sender.displayName || profile.accountHolder || sender.accountHolder || '',
    senderStreet: profile.senderStreet || sender.street || '',
    senderZip: profile.senderZip || sender.zip || '',
    senderCity: profile.senderCity || sender.city || '',
    senderCountry: profile.senderCountry || 'DE',
    senderEmail: profile.senderEmail || sender.email || '',
    senderPhone: profile.senderPhone || sender.phone || '',
  };
}

/** Absender / Mandantendaten aus App-Store (Stammdaten) */
export function getPeInvoiceSenderFromAppStore(): PeInvoiceSender {
  const cd = (useAppStore.getState() as any)?.clientData || {};
  const nested = cd.clientData || {};
  const displayName =
    cd.salonname ||
    nested.salonname ||
    cd.firmenname ||
    nested.firmenname ||
    cd.unternehmen ||
    nested.unternehmen ||
    cd.unternehmername ||
    nested.unternehmername ||
    '';
  const accountHolder =
    cd.kontoinhaber || nested.kontoinhaber || displayName || '';

  return {
    displayName: String(displayName || '').trim(),
    accountHolder: String(accountHolder || '').trim(),
    street: String(cd.strasse || nested.strasse || '').trim(),
    zip: String(cd.plz || nested.plz || '').trim(),
    city: String(cd.ort || nested.ort || '').trim(),
    email: String(cd.mailadresse || nested.mailadresse || '').trim(),
    phone: String(cd.whatsappNr || nested.whatsappNr || '').trim(),
    clientIban: String(cd.iban || nested.iban || '').trim(),
  };
}

/** Kalkulator-State wie auf KalkulatorPage (clientData.kalkulator). */
export function getKalkulatorStateFromAppStore(): Record<string, unknown> {
  const cd = (useAppStore.getState() as any)?.clientData || {};
  const nested = cd.clientData || {};
  return (cd.kalkulator || nested.kalkulator || {}) as Record<string, unknown>;
}

/**
 * Beide Verrechnungssätze aus Kalkulator Preis-Barometer (Coach-Soll / Empfehlung):
 * - exclusive = barometerStundenfaktor (ohne Material) — Box „Wir empfehlen dir den Stundenfaktor“
 * - inclusive = Stundensatz inkl. Material aus priceParameterListe
 *
 * Bewusst NICHT echterStundensatz / echterStundensatzMat (Mandanten-IST).
 */
export function getBarometerHourlyRatesFromAppStore(): {
  exclusive: number;
  inclusive: number;
} {
  const kalk = getKalkulatorStateFromAppStore();

  const barometer = Number(kalk.barometerStundenfaktor ?? 0);
  const exclusive =
    Number.isFinite(barometer) && barometer > 0 ? barometer : 0;

  const list = Array.isArray(kalk.priceParameterListe) ? kalk.priceParameterListe : [];
  const matParam =
    list.find((p: any) => {
      const desc = String(p?.desc || '').toLowerCase();
      return desc.includes('stundensatz') || desc.includes('verrechnungssatz');
    }) ||
    list.find((p: any) => p?.id === 'p4') ||
    null;
  const targetMat = Number(matParam?.preis ?? 0);
  const inclusive =
    Number.isFinite(targetMat) && targetMat > 0 ? targetMat : 0;

  return { exclusive, inclusive };
}

/** Aktiver Barometer-Satz je Material-Modus */
export function getBarometerHourlyRateFromAppStore(
  materialMode: 'inclusive' | 'exclusive' = 'exclusive'
): number {
  const rates = getBarometerHourlyRatesFromAppStore();
  return materialMode === 'inclusive' ? rates.inclusive : rates.exclusive;
}
